import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getCurrentUser } from "../services/authService";
import { isKnownTarget, resolveNotificationTarget } from "../services/notificationTargets";

import {
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../services/notificationService";
import { onForegroundPush, pushPermission, syncPush } from "../services/push";

/*
 * The signed-in user's notifications — platform-admin broadcasts and stored
 * catalogue events alike: the bell's unread
 * count, the foreground popups, and the push permission. Mounted only inside
 * the authenticated layout.
 */
const NotificationsContext = createContext(null);

const MAX_POPUPS = 4;

/** A push's logical target, from its string-only `data`. */
const targetFrom = (data) => {
  if (!data.target_type) return null;
  let params = {};
  try {
    params = data.target_params ? JSON.parse(data.target_params) : {};
  } catch {
    params = {};
  }
  return { type: data.target_type, id: data.target_id || null, params };
};

export function NotificationsProvider({ children }) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [popups, setPopups] = useState([]);
  const [permission, setPermission] = useState("unsupported");
  // Bumped when a push arrives, so an open bell panel reloads its list.
  const [listVersion, setListVersion] = useState(0);
  // Bumped to ask the bell to open its panel, e.g. from a popup click.
  const [panelRequest, setPanelRequest] = useState(0);
  const language = useRef(i18n.language);
  // Notification ids already shown, so one push is one popup and one +1.
  const seen = useRef(new Set());

  const refreshCount = useCallback(() => {
    fetchUnreadCount()
      .then(setUnread)
      .catch(() => {});
  }, []);

  // Count once on load; from then on pushes and read calls keep it current.
  // FCM does not replay what arrived while the portal was closed, so this is
  // the one fetch that catches up.
  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  // Push: ask once after login, and pick up a refreshed token on every load.
  useEffect(() => {
    pushPermission().then(setPermission);
    syncPush(language.current, { prompt: true }).then(setPermission);
  }, []);

  // Re-register in the new language so the OS notification follows the UI.
  useEffect(() => {
    const onLanguage = (lang) => {
      language.current = lang;
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        syncPush(lang);
      }
    };
    i18n.on("languageChanged", onLanguage);
    return () => i18n.off("languageChanged", onLanguage);
  }, [i18n]);

  /*
   * One push, from either path, counted once by its notification id. The
   * badge takes the backend's own `unread_count` when it sends one, else +1.
   * With `popup`, it also becomes a popup: text from the bilingual `data`
   * fields, falling back to the `notification` block, then `data.title/body`.
   */
  const receive = useCallback((payload, { popup }) => {
    const { notification = {}, data = {} } = payload ?? {};
    const id = data.notification_id || null;
    if (id) {
      if (seen.current.has(id)) return;
      seen.current.add(id);
    }

    const count = Number.parseInt(data.unread_count, 10);
    setUnread((current) => (Number.isNaN(count) ? current + 1 : count));
    setListVersion((v) => v + 1);

    if (!popup) return;

    const title = notification.title || data.title || "";
    const body = notification.body || data.body || "";
    const next = {
      key: `${id ?? "push"}-${Date.now()}`,
      id,
      url: data.url || null,
      target: targetFrom(data),
      branchId: data.branch_id || null,
      title: { en: data.title_en || title, ar: data.title_ar || title },
      body: { en: data.body_en || body, ar: data.body_ar || body },
    };
    if (next.title.en || next.body.en) {
      setPopups((current) => [...current, next].slice(-MAX_POPUPS));
    }
  }, []);

  // Foreground: FCM hands the push to onMessage and shows no OS notification.
  useEffect(() => onForegroundPush((payload) => receive(payload, { popup: true })), [receive]);

  // Background: the worker has shown the OS notification and forwards the
  // payload to open tabs, so a hidden tab's badge is current on return.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const onWorkerMessage = (event) => {
      if (event.data?.source === "fcm-background") receive(event.data.payload, { popup: false });
    };
    navigator.serviceWorker.addEventListener("message", onWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onWorkerMessage);
  }, [receive]);

  const markRead = useCallback((id) => {
    if (!id) return;
    markNotificationRead(id)
      .then(setUnread)
      .catch(() => {});
  }, []);

  const markAllRead = useCallback(
    () =>
      markAllNotificationsRead()
        .then(setUnread)
        .catch(() => {}),
    []
  );

  /** A local popup (no push behind it), e.g. "that item is no longer available". */
  const notice = useCallback((title, body = "") => {
    const text = { en: title, ar: title };
    setPopups((current) =>
      [
        ...current,
        {
          key: `notice-${Date.now()}`,
          id: null,
          url: null,
          target: null,
          title: text,
          body: { en: body, ar: body },
        },
      ].slice(-MAX_POPUPS)
    );
  }, []);

  /*
   * Open a stored notification: mark it read, then go to its target — or to
   * the type's list page with a short notice when the target is gone or not
   * allowed. Never performs an action. Returns false when there is nowhere to
   * go (a broadcast, or an unknown type), so the caller keeps its own default.
   */
  const openTarget = useCallback(
    async ({ id, target, branchId }, { replace = false } = {}) => {
      if (id) markRead(id);
      if (!isKnownTarget(target)) return false;

      const resolved = await resolveNotificationTarget(target, { user: getCurrentUser(), branchId });
      if (!resolved) return false;

      navigate(resolved.path, { replace });
      if (resolved.missing) notice(t("notifications.targetMissing"));
      return true;
    },
    [markRead, navigate, notice, t]
  );

  const dismissPopup = useCallback((key) => {
    setPopups((current) => current.filter((p) => p.key !== key));
  }, []);

  const openPanel = useCallback(() => setPanelRequest((v) => v + 1), []);

  const enablePush = useCallback(() => syncPush(language.current, { force: true }).then(setPermission), []);

  const value = useMemo(
    () => ({
      unread,
      popups,
      permission,
      listVersion,
      panelRequest,
      refreshCount,
      markRead,
      markAllRead,
      dismissPopup,
      openPanel,
      enablePush,
      openTarget,
    }),
    [
      unread,
      popups,
      permission,
      listVersion,
      panelRequest,
      refreshCount,
      markRead,
      markAllRead,
      dismissPopup,
      openPanel,
      enablePush,
      openTarget,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export const useNotifications = () => useContext(NotificationsContext);

/** Title or body in the active UI language, falling back to the other one. */
export const localized = (pair, language) =>
  (language === "ar" ? pair?.ar || pair?.en : pair?.en || pair?.ar) ?? "";
